'use client'

import dayjs from 'dayjs'
import 'dayjs/locale/tr'
import isoWeek from 'dayjs/plugin/isoWeek'
import { LocalStorageHelper } from 'helpers/localStorageHelper'
import { useUserData } from 'contexts/UserDataContext'
import { useEffect, useState } from 'react'

dayjs.extend(isoWeek)
dayjs.locale('tr')

import {
  IDailyReadingTime,
  formatDuration,
  cleanupOldData,
  groupDataForDisplay,
  STORAGE_KEYS
} from 'helpers/readingTimeHelpers'

export default function DailyReadingTime() {
  const { userData } = useUserData()
  const [dailyTimes, setDailyTimes] = useState<IDailyReadingTime>({})
  const [totalTime, setTotalTime] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Load and cleanup data
    const data =
      LocalStorageHelper.get<IDailyReadingTime>(STORAGE_KEYS.DAILY_READING_TIME) || {}
    const cleanedData = cleanupOldData(data)

    // Save cleaned data if different
    if (Object.keys(cleanedData).length !== Object.keys(data).length) {
      LocalStorageHelper.set(STORAGE_KEYS.DAILY_READING_TIME, cleanedData)
    }

    setDailyTimes(cleanedData)

    // Use cloud total if available, otherwise fallback to local
    const cloudTotal = userData.readingTime?.total
    const localTotal =
      LocalStorageHelper.get<number>(STORAGE_KEYS.TOTAL_READING_TIME) || 0

    setTotalTime(cloudTotal !== undefined ? cloudTotal : localTotal)
    setIsLoading(false)

    // Listen to storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.DAILY_READING_TIME && e.newValue !== null) {
        setDailyTimes(JSON.parse(e.newValue))
      }
      if (e.key === STORAGE_KEYS.TOTAL_READING_TIME && e.newValue !== null) {
        if (userData.readingTime?.total === undefined) {
          setTotalTime(JSON.parse(e.newValue))
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [userData.readingTime])

  // Update totalTime when cloud data changes
  useEffect(() => {
    if (userData.readingTime?.total !== undefined) {
      setTotalTime(userData.readingTime.total)
    }
  }, [userData.readingTime?.total])

  // Get last 7 days for chart
  const last7Days = Array.from({ length: 7 }, (_, i) =>
    dayjs().subtract(i, 'day').format('YYYY-MM-DD'),
  ).reverse()

  const maxTime = Math.max(...last7Days.map((date) => dailyTimes[date] || 0), 1)

  // Get grouped entries for display
  const displayEntries = groupDataForDisplay(dailyTimes)
  const hasData = displayEntries.length > 0

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="border-4 border-blue-500 border-t-transparent rounded-full w-8 h-8 animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-linear-to-r from-blue-50 dark:from-blue-900/20 to-cyan-50 dark:to-cyan-900/20 p-4 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="flex justify-center items-center bg-blue-100 dark:bg-blue-900/40 rounded-full w-12 h-12">
            <span className="text-2xl">⏱️</span>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Toplam Okuma Süresi
            </p>
            <p className="font-bold text-xl">{formatDuration(totalTime)}</p>
          </div>
        </div>
      </div>

      {/* Last 7 Days Chart */}
      {hasData && (
        <div>
          <h3 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
            Son 7 Gün
          </h3>
          <div className="flex justify-between items-end gap-2 bg-gray-50 dark:bg-gray-700/50 pt-8 px-4 pb-4 rounded-lg h-44">
            {last7Days.map((date) => {
              const seconds = dailyTimes[date] || 0
              const heightPercent = (seconds / maxTime) * 100
              const dayName = dayjs(date).format('ddd')
              const barHeight = Math.max(heightPercent, seconds > 0 ? 5 : 0)

              return (
                <div key={date} className="flex flex-col flex-1 items-center h-full">
                  <div className="relative flex flex-col justify-end items-center w-full h-[calc(100%-24px)]">
                    {seconds > 0 && (
                      <span className="bottom-full mb-1 absolute text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                        {Math.floor(seconds / 60)}dk
                      </span>
                    )}
                    <div
                      className="bg-linear-to-t from-blue-500 to-cyan-400 rounded-t-sm w-full transition-all duration-300"
                      style={{ height: `${barHeight}%` }}
                    />
                  </div>
                  <span className="mt-2 font-medium text-gray-600 dark:text-gray-400 text-xs capitalize">
                    {dayName}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Daily/Weekly Breakdown */}
      <div>
        <h3 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">Detay</h3>
        {hasData ? (
          <div className="space-y-2">
            {displayEntries.map((entry, index) => (
              <div
                key={index}
                className="flex justify-between items-center bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex justify-center items-center bg-blue-100 dark:bg-blue-900/40 rounded-full w-8 h-8 text-sm">
                    {entry.type === 'day' ? '📖' : '📅'}
                  </div>
                  <div>
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {entry.label}
                    </span>
                    {entry.dateRange && (
                      <span className="block text-gray-500 dark:text-gray-400 text-xs">
                        {entry.dateRange}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full font-semibold text-sm ${
                    entry.type === 'week'
                      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
                      : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                  }`}
                >
                  {formatDuration(entry.seconds)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg text-center">
            <div className="mb-3 text-4xl">📚</div>
            <p className="text-gray-600 dark:text-gray-400">
              Henüz okuma süresi kaydı bulunmuyor.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-500 text-sm">
              Kronometreyi kullanarak okuma sürenizi takip edebilirsiniz.
            </p>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/10 px-3 py-2 rounded-lg text-amber-700 dark:text-amber-300 text-sm">
        <span>💡</span>
        <p>
          Okuma süresi, kronometrenin bulunduğu sayfalarda geçirdiğiniz süreyi takip eder.
          Sadece kronometre ekranda görünürken süre sayılır.
        </p>
      </div>
    </div>
  )
}
